import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddEntityPhoneLink, RemoveEntityPhoneLink, UpdateEntity } from './entity.actions';
import { EntityState } from './entity.state';
import {
    AddPhone,
    ClearThirdPartyPhone,
    ClonePhone,
    DeletePhone,
    RestorePhone,
    SetThirdPartyPhones,
    UpdatePhone
} from './phone.actions';

export interface PhoneStateModel {
    phone: any;
    currentID: string;
}
// add currentID?

@State<PhoneStateModel>({
    name: 'phoneState',
    defaults: {
        phone: {},
        currentID: null
    }
})
@Injectable()
export class PhoneState {
    constructor(private store: Store, private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static fullState(state: PhoneStateModel) {
        //temporary selector for debugging
        return state.phone;
    }

    @Selector()
    static phoneByID(state: PhoneStateModel) {
        return (phoneID: string) => {
            if (state.phone[phoneID]) {
                return state.phone[phoneID];
            } else {
                return {};
            }
        };
    }

    @Selector()
    static currentPhone(state: PhoneStateModel) {
        if (state.phone[state.currentID]) {
            return state.phone[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static Phones(state: PhoneStateModel) {
        //return all Phonees
        let result: any = [];
        for (var i in state.phone) {
            result.push(state.phone[i]);
        }
        return result;
    }

    @Selector()
    static phonesByIDs(state: PhoneStateModel) {
        //return Phonees based on an array of Phone_ID passed in
        return (phoneIDs: any[]) => {
            let result: any = [];
            if (phoneIDs) {
                phoneIDs.forEach(function (thisID) {
                    result.push(state.phone[thisID]);
                });
            }
            return result;
        };
    }

    //******  ACTIONS  ******

    @Action(UpdatePhone)
    updatePhone(sc: StateContext<PhoneStateModel>, action: UpdatePhone) {
        const state = sc.getState();
        let phoneID = action.phone.Phone_ID;
        let updatePhone: any = { ...action.phone }; //need DEEP copy?  -- subloc list????
        const currentPhone = state.phone[phoneID];
        this.helper.setItemChanged(updatePhone, state.phone[phoneID].Edit_Status);

        sc.patchState({
            phone: {
                ...state.phone,
                [phoneID]: {
                    ...state.phone[phoneID],
                    ...updatePhone
                }
            }
        });

        if (action.entityID) {
            this.store.dispatch(new UpdateEntity({ Entity_ID: action.entityID }));
        }

        // If Phone record's Dflt_Phone_Ind is changed to "Y", Set to "N" for all other Phone Records.
        if (updatePhone.Dflt_Phone_Ind != currentPhone.Dflt_Phone_Ind && updatePhone.Dflt_Phone_Ind == 'Y') {
            let entity = this.store.selectSnapshot(EntityState.entityByID)(action.entityID);
            let phoneIDs = [];
            if (entity) {
                if (entity.Phones && entity.Phones.Phone) {
                    for (let i in entity.Phones.Phone) {
                        phoneIDs.push(entity.Phones.Phone[i]);
                    }
                }
            }

            if (phoneIDs.length > 0) {
                let updateStatePhone = cloneDeep(state.phone);
                for (let i in updateStatePhone) {
                    let id = updateStatePhone[i].Phone_ID;
                    if (phoneIDs.indexOf(id) === -1) {
                        continue;
                    } else {
                        this.helper.setItemChanged(updateStatePhone[i], updateStatePhone[i].Edit_Status);
                        if (updateStatePhone[i].Phone_ID == phoneID) {
                            updateStatePhone[i].Dflt_Phone_Ind = 'Y';
                        } else {
                            updateStatePhone[i].Dflt_Phone_Ind = 'N';
                        }
                    }
                }
                sc.patchState({ phone: updateStatePhone });
            }
        }
    }

    @Action(DeletePhone)
    deletePhone(sc: StateContext<PhoneStateModel>, action: DeletePhone) {
        const state = sc.getState();
        let phoneID = action.phoneID;
        let phone = { ...state.phone }; //shallow copy Phone from state
        this.helper.setItemDeleted(phone, phoneID);

        sc.patchState({ phone: phone });
        this.store.dispatch(new RemoveEntityPhoneLink(action.entityID, action.phoneID));
    }

    @Action(RestorePhone)
    restorePhone(sc: StateContext<PhoneStateModel>, action: RestorePhone) {
        const state = sc.getState();
        let phoneID = action.phoneID;
        let phone = { ...state.phone }; //shallow copy Phone from state
        this.helper.setItemRestored(phone, phoneID);

        sc.patchState({ phone: phone });
    }

    @Action(AddPhone)
    addPhone(sc: StateContext<PhoneStateModel>, action: AddPhone) {
        const state = sc.getState();
        let newPhoneID: any = this.helper.getTempID();
        let newPhone = this.helper.createDefaultItem(ContractObjectDefaults, 'Phone', action.phone);
        newPhone.Phone_ID = newPhoneID;
        this.helper.setItemAdded(newPhone);

        //set default ind and phone type on first phone for that entity
        let phoneRecordsExist: boolean = false;

        let entity = this.store.selectSnapshot(EntityState.entityByID)(action.entityID);
        if (entity) {
            if (entity.Phone || (entity.Phones && entity.Phones.Phone)) {
                phoneRecordsExist = true;
            }
        }

        if (phoneRecordsExist == false) {
            newPhone.Dflt_Phone_Ind = 'Y';
            newPhone.Phone_Type = 'BS';
        }

        sc.patchState({
            currentID: newPhoneID,
            phone: { ...state.phone, [newPhoneID]: newPhone }
        });

        this.store.dispatch(new AddEntityPhoneLink(action.entityID, newPhoneID));
    }

    @Action(ClonePhone)
    clonePhone(sc: StateContext<PhoneStateModel>, action: ClonePhone) {
        const state = sc.getState();
        let srcID = action.phoneID;
        let newPhoneID: any = this.helper.getTempID();
        let newPhone = cloneDeep(state.phone[srcID]);
        newPhone.Phone_ID = newPhoneID;
        this.helper.setItemAdded(newPhone);

        if (state.phone[srcID].Edit_Status != EditStatusType.Delete) {
            newPhone.Phone_Type = null; // if src phone not deleted then clear phone type on new phone
            if (state.phone[srcID].Dflt_Phone_Ind == 'Y') {
                newPhone.Dflt_Phone_Ind = 'N';
            }
        }

        sc.patchState({
            currentID: newPhoneID,
            phone: { ...state.phone, [newPhoneID]: newPhone }
        });

        this.store.dispatch(new AddEntityPhoneLink(action.entityID, newPhoneID));
    }

    @Action(SetThirdPartyPhones)
    setThirdPartyPhones(sc: StateContext<PhoneStateModel>, action: SetThirdPartyPhones) {
        const state = sc.getState();
        const phones = cloneDeep(state.phone);
        let newPhones = cloneDeep(action.phones);

        for (var i in newPhones) {
            newPhones[i].Edit_Status = EditStatusType.NoChange;
            phones[newPhones[i].Phone_ID] = newPhones[i];
        }
        sc.patchState({
            phone: phones
        });
    }

    @Action(ClearThirdPartyPhone)
    clearThirdPartyPhone(sc: StateContext<PhoneStateModel>, action: ClearThirdPartyPhone) {
        const state = sc.getState();
        let phone = { ...state.phone }; //shallow copy Phone from state
        delete phone[action.phoneID];
        sc.patchState({ phone: phone });
    }
}
